import { Component } from '@verndale/core';
import YouTubeIframeLoader from 'youtube-iframe';
import { BREAKPOINTS } from '../helpers/breakpoints';
import { debounce } from '../helpers/debounce';
import { DataLayerObject, ExtendedWindowType } from '../helpers/global-types';

type YoutubePlayer = {
  on: (event: string, cb: () => void) => void;
  showLightbox: () => void;
  setVolume: (newVolume: number) => void;
  playVideo: () => void;
  pauseVideo: () => void;
  getCurrentTime: () => number;
  getDuration: () => number;
  loadVideoById: (id: string, start: number, quality: string) => void;
};

class VideoFigure extends Component {
  private player: YoutubePlayer | null = null;
  private src: string;
  private pauseLabel: string;
  private playLabel: string;
  private autoplay: boolean;
  private playing = false;
  private lastEvent: number | null;
  private config: object;
  private customControls: boolean;
  private mobileVideoId: string;
  private tabletVideoId: string;
  private wideVideoId: string;
  private currentBreakpoint: number;
  private changed: boolean;
  private checkProgress: null | NodeJS.Timer;
  private playingInterval: null | NodeJS.Timer;
  private fallback: number;
  private playedVideoTimes: number[];
  private iframeLoaded: boolean;
  private facadeEvents: string[];

  constructor(el: HTMLElement) {
    super(el);
    this.src = this.el.dataset.url || '';
    this.pauseLabel = this.el.dataset.pauseLabel || 'Pause';
    this.playLabel = this.el.dataset.playLabel || 'Play';
    this.autoplay = this.el.dataset.autoplay ? true : false;
    this.customControls = this.el.dataset.customControls ? true : false;
    this.mobileVideoId = this.el.dataset.mobileVideoId || '';
    this.tabletVideoId =
      this.el.dataset.tabletVideoId || this.el.dataset.mobileVideoId || '';
    this.wideVideoId = this.el.dataset.wideVideoId || this.src || '';
    this.currentBreakpoint = 0;
    this.fallback = 0;
    this.playing = false;
    this.lastEvent = null;
    this.config = {};
    this.changed = false;
    this.playingInterval = null;
    this.checkProgress = null;
    this.playedVideoTimes = [];
    this.iframeLoaded = false;
    this.facadeEvents = [
      'resize',
      'mousemove',
      'touchstart',
      'touchmove',
      'scroll',
      'click',
      'keydown'
    ];

    if (this.wideVideoId || this.tabletVideoId || this.mobileVideoId) {
      this.resizeYoutubePlayer();
    }

    this.addFacadeListeners();
  }

  setupDefaults() {
    this.dom = {
      el: this.el,
      videoTrigger: this.el.querySelector<HTMLButtonElement>(
        '.figure__media-button'
      ),
      localVideo: this.el.querySelector<HTMLVideoElement>('video'),
      captionOverlay: this.el.querySelector<HTMLElement>(
        '.figure__caption--overlay'
      ),
      youtubeImg: this.el.querySelector<HTMLElement>(
        '.figure__media-youtube-img'
      ),
      iframe: this.el.querySelector<HTMLElement>('.iframe'),
      iframeContainer: this.el.querySelector<HTMLElement>('.figure__media')
    };

    if (this.dom.localVideo && this.autoplay) {
      (this.dom.localVideo as HTMLVideoElement).muted = true;
      this.handleVideoClick();
    }
  }

  addListeners() {
    (this.dom.videoTrigger as HTMLButtonElement)?.addEventListener(
      'click',
      this.handleVideoClick.bind(this)
    );

    if (this.dom.localVideo) {
      (this.dom.localVideo as HTMLVideoElement).addEventListener(
        'play',
        this.handleVideoPlay.bind(this)
      );
      (this.dom.localVideo as HTMLVideoElement).addEventListener(
        'pause',
        this.handleVideoPause.bind(this)
      );
      (this.dom.localVideo as HTMLVideoElement).addEventListener(
        'ended',
        this.handleVideoEnd.bind(this)
      );
    }

    this.mobileVideoId = this.el.dataset.mobileVideoId || '';
    this.tabletVideoId =
      this.el.dataset.tabletVideoId || this.el.dataset.mobileVideoId || '';
    this.wideVideoId = this.el.dataset.wideVideoId || this.src || '';

    if (
      this.dom.iframe &&
      (this.mobileVideoId || this.tabletVideoId || this.wideVideoId)
    ) {
      window.addEventListener(
        'resize',
        debounce(this.handleYoutubeResize.bind(this), 250)
      );
    }
  }

  addFacadeListeners() {
    this.facadeEvents.forEach(event => {
      window.addEventListener(event, this.handleWindowInteraction.bind(this));
    });
  }

  removeFacadeListeners() {
    this.facadeEvents.forEach(event => {
      window.removeEventListener(
        event,
        this.handleWindowInteraction.bind(this)
      );
    });
  }

  resizeYoutubePlayer() {
    if (this.player) {
      if (
        window.matchMedia(`(max-width: ${BREAKPOINTS.tablet - 1}px)`).matches &&
        this.currentBreakpoint !== BREAKPOINTS.mobile &&
        this.mobileVideoId
      ) {
        this.player?.loadVideoById(this.mobileVideoId, 0, 'large');
        this.currentBreakpoint = BREAKPOINTS.mobile;
        return;
      }

      if (
        window.matchMedia(
          `(min-width:${BREAKPOINTS.tablet}px) and (max-width: ${
            BREAKPOINTS.tabletLandscape - 1
          }px)`
        ).matches &&
        this.currentBreakpoint !== BREAKPOINTS.tablet &&
        this.tabletVideoId
      ) {
        this.player?.loadVideoById(this.tabletVideoId, 0, 'large');
        this.currentBreakpoint = BREAKPOINTS.tablet;
        return;
      }

      if (
        window.matchMedia(
          `(min-width:${BREAKPOINTS.tabletLandscape}px) and (max-width: ${
            BREAKPOINTS.wide - 1
          }px)`
        ).matches &&
        this.currentBreakpoint !== BREAKPOINTS.tabletLandscape &&
        this.src
      ) {
        this.player?.loadVideoById(this.src, 0, 'large');
        this.currentBreakpoint = BREAKPOINTS.tabletLandscape;
        return;
      }

      if (
        window.matchMedia(`(min-width: ${BREAKPOINTS.wide}px)`).matches &&
        this.currentBreakpoint !== BREAKPOINTS.wide &&
        this.wideVideoId
      ) {
        this.player?.loadVideoById(this.wideVideoId, 0, 'large');
        this.currentBreakpoint = BREAKPOINTS.wide;
      }
    }
  }

  playVideoEvent() {
    const videoPlayerPlay = this.el.dataset.videoPlayerPlay;
    if (videoPlayerPlay) {
      (window as ExtendedWindowType).dataLayer.push(
        JSON.parse(videoPlayerPlay) as DataLayerObject
      );
    }
  }

  progressVideoEvent(iframe: YoutubePlayer) {
    if (iframe && iframe.getCurrentTime && iframe.getDuration) {
      const progress = Math.floor(
        (iframe?.getCurrentTime() * 100) / iframe?.getDuration()
      );
      let progressVal = 0;
      if (progress === 75) {
        progressVal = 75;
      } else if (progress === 50) {
        progressVal = 50;
      } else if (progress === 25) {
        progressVal = 25;
      } else if (progress === 10) {
        progressVal = 10;
      }

      if (progressVal > 0) {
        if (!this.playedVideoTimes.includes(progressVal)) {
          this.playedVideoTimes.push(progressVal);
          const videoPlayerProgress =
            this.el.dataset.videoPlayerProgress?.replace(
              '{progress}',
              progressVal.toString()
            );
          if (videoPlayerProgress) {
            (window as ExtendedWindowType).dataLayer.push(
              JSON.parse(videoPlayerProgress) as DataLayerObject
            );
          }
        }
      }
    }
  }

  endVideoEvent() {
    this.playedVideoTimes = [];
    const videoPlayerEnd = this.el.dataset.videoPlayerEnd;
    if (videoPlayerEnd) {
      (window as ExtendedWindowType).dataLayer.push(
        JSON.parse(videoPlayerEnd) as DataLayerObject
      );
    }
  }

  loadIframe() {
    if (this.dom.iframe) {
      YouTubeIframeLoader.load(this.handleIframeLoader.bind(this));
      (this.dom.iframe as HTMLElement).setAttribute('tabindex', '-1');
      this.iframeLoaded = true;
      this.removeFacadeListeners();
    }
  }

  handleWindowInteraction() {
    if (!this.iframeLoaded) this.loadIframe();
  }

  handleYoutubeResize() {
    this.resizeYoutubePlayer();
  }

  handleIframeLoader(YT: {
    Player: new (
      arg0: HTMLElement | NodeListOf<HTMLElement> | null,
      arg1: {
        height: string;
        width: string;
        videoId: string;
        playerVars: object;
        events: {
          onStateChange: (event: { data: number }) => void;
          onReady: () => void;
        };
      }
    ) => YoutubePlayer | null;
  }) {
    this.config = {
      modestbranding: 1,
      rel: 0,
      showinfo: 0,
      autohide: 1,
      playsinline: 1
    };

    if (this.autoplay) {
      this.config = {
        modestbranding: 1,
        rel: 0,
        showinfo: 0,
        autohide: 1,
        playsinline: 1,
        autoplay: 1,
        mute: 1
      };
    }

    if (this.customControls) {
      this.config = {
        ...this.config,
        disable: 1,
        iv_load_policy: 3,
        loop: 1,
        ecver: 2
      };
    }

    this.player = new YT.Player(this.dom.iframe, {
      height: '390',
      width: '640',
      videoId: this.src,
      playerVars: this.config,
      events: {
        onStateChange: this.handleStateChange.bind(this),
        onReady: this.handlePlayerReady.bind(this)
      }
    });

    if (this.autoplay) {
      this.handleVideoPlay();
    }
  }

  handlePlayerReady() {
    if (this.customControls) {
      this.resizeYoutubePlayer();
    }
  }

  handleStateChange(event: { data: number }) {
    this.lastEvent = event.data;
    if (event.data === 0) {
      setTimeout(() => {
        this.handleVideoPause();
        this.player?.playVideo?.();
        this.endVideoEvent();
      }, 100);
    }

    if (event.data === 1) {
      if (!this.changed) {
        this.handleVideoPlay();
        this.player?.playVideo?.();
      } else {
        this.player?.pauseVideo?.();
        this.changed = false;
      }
    }

    if (event.data === 2) {
      setTimeout(() => this.handleVideoPause(), 250);
    }
  }

  async handleVideoClick() {
    if (this.el.getAttribute('data-prevent-playing') !== 'true') {
      if (this.dom.localVideo) {
        if ((this.dom.localVideo as HTMLVideoElement).paused) {
          (this.dom.localVideo as HTMLVideoElement).play();
          (this.dom.localVideo as HTMLVideoElement).setAttribute(
            'controls',
            'true'
          );
        } else {
          (this.dom.localVideo as HTMLVideoElement).pause();
          (this.dom.localVideo as HTMLVideoElement).removeAttribute('controls');
        }
      }

      if (this.dom.iframe) {
        if (this.player) {
          if (!this.playing) {
            const _iframe = this.dom.iframe as HTMLIFrameElement;
            if (_iframe.getAttribute('tabindex') === '-1') {
              _iframe.setAttribute('tabindex', '0');
            }
            this.player.playVideo?.();
            this.playing = true;
          } else {
            this.player.pauseVideo?.();
            this.handleVideoPause();
            this.playing = false;
          }
        }
      }
    }
  }

  handleExternalVideoPause() {
    if (this.playing) {
      this.player?.pauseVideo?.();
      this.playing = false;
    }
  }

  handleReplaceId(id: string, autoplay = false) {
    this.player?.loadVideoById(id, 0, '');
    this.changed = !autoplay;
  }

  handleRemoveImage() {
    if (this.player && this.player?.getCurrentTime() > 0.1) {
      this.el.classList.add('figure-video--playing');
      (this.dom.youtubeImg as HTMLElement)?.setAttribute(
        'style',
        'display:none'
      );

      (this.dom.videoTrigger as HTMLElement)?.classList.add(
        'figure__media-button--pause'
      );

      if (this.playingInterval) {
        clearInterval(this.playingInterval);
      }
    } else {
      this.fallback++;
    }
    if (this.fallback > 500) {
      if (this.playingInterval) {
        clearInterval(this.playingInterval);
      }
    }
  }

  handleVideoPlay() {
    this.playVideoEvent();
    this.playedVideoTimes = [];
    const videoTrigger = this.dom.videoTrigger as HTMLVideoElement;

    if (this.customControls) {
      if (!this.autoplay) {
        videoTrigger.classList.add('figure__media-button--pause');
      }
    } else {
      (this.dom.iframeContainer as HTMLElement)
        ?.querySelector('.iframe')
        ?.setAttribute('tabindex', '0');
    }
    if (this.dom.iframe) {
      const ytIframe = this.el.querySelector<HTMLIFrameElement>('iframe');
      if (!this.customControls) {
        ytIframe?.focus();
      }
      this.progressVideoEvent(this.player as YoutubePlayer);

      if (this.autoplay && this.playingInterval === null) {
        this.playingInterval = setInterval(() => {
          this.handleRemoveImage();
        }, 100);
      } else {
        this.el.classList.add('figure-video--playing');
        (this.dom.youtubeImg as HTMLElement)?.setAttribute(
          'style',
          'display:none'
        );
      }

      videoTrigger.setAttribute('aria-label', this.pauseLabel);

      if (this.checkProgress) {
        clearInterval(this.checkProgress);
      }
      this.checkProgress = setInterval(() => {
        this.progressVideoEvent(this.player as YoutubePlayer);
      }, 1100);
    }

    this.playing = true;
  }

  handleVideoPause() {
    if (this.checkProgress) {
      clearInterval(this.checkProgress);
    }
    const videoTrigger = this.dom.videoTrigger as HTMLVideoElement;
    if (this.lastEvent === 2 || this.lastEvent === 0) {
      if (this.dom.iframe) {
        videoTrigger.setAttribute('aria-label', this.playLabel);

        if (this.customControls) {
          videoTrigger.classList.remove('figure__media-button--pause');
          this.el.classList.remove('figure-video--playing');
        }
      }
      this.playing = false;
    } else {
      if (this.dom.localVideo) {
        (this.dom.localVideo as HTMLVideoElement).removeAttribute('controls');
        (this.dom.videoTrigger as HTMLVideoElement).setAttribute(
          'tabindex',
          '0'
        );
      }
    }
  }

  handleVideoEnd() {
    this.el.classList.remove('figure-video--playing');

    if (this.dom.localVideo) {
      (this.dom.localVideo as HTMLVideoElement).currentTime = 0;
      (this.dom.localVideo as HTMLVideoElement).load();
      (this.dom.localVideo as HTMLVideoElement).removeAttribute('controls');
    }
  }
}

export default VideoFigure;
